import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const GuidesSection = () => {
  const [activeGuide, setActiveGuide] = useState(null);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState([]);
  const [integrationVisible, setIntegrationVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const headerObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) { setHeaderVisible(true); headerObserver.disconnect(); }
      },
      { threshold: 0.2 }
    );
    const cardsObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          [0, 1, 2, 3].forEach((i) => {
            setTimeout(() => setCardsVisible(prev => [...prev, i]), i * 150);
          });
          cardsObserver.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    const integrationObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) { setIntegrationVisible(true); integrationObserver.disconnect(); }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) {
      const headerEl = sectionRef.current.querySelector('#guides-header');
      const cardsEl = sectionRef.current.querySelector('#guides-grid');
      const integrationEl = sectionRef.current.querySelector('#guides-integration');
      if (headerEl) headerObserver.observe(headerEl);
      if (cardsEl) cardsObserver.observe(cardsEl);
      if (integrationEl) integrationObserver.observe(integrationEl);
    }
    return () => { headerObserver.disconnect(); cardsObserver.disconnect(); integrationObserver.disconnect(); };
  }, []);

  const guides = [
    {
      id: 'morning',
      title: 'Morning Intentions',
      icon: 'Sun',
      color: 'from-yellow-500/20 to-orange-500/20',
      borderColor: 'border-yellow-500/30',
      iconColor: 'text-yellow-500',
      description: 'Start your day aligned with empowering guidance and affirmations that support your real-life situation.',
      features: [
        'Personalized guidance built around your actual goals',
        'Affirmations in your own cloned voice',
        'Journaling to clarify your intentions',
        'Strengthens your focus for the day ahead'
      ],
      ritualNote: 'Journaling helps clarify your intentions and strengthens your focus for the day ahead.'
    },
    {
      id: 'shadow',
      title: 'Shadow Work',
      icon: 'Mask',
      color: 'from-purple-500/20 to-indigo-500/20',
      borderColor: 'border-purple-500/30',
      iconColor: 'text-purple-500',
      description: 'Explore emotional patterns and triggers to uncover hidden beliefs and unconscious habits.',
      features: [
        'Guided exploration of limiting beliefs',
        'Safe space for processing difficult emotions',
        'Surfaces what is hidden — transforms discomfort into awareness',
        'Grounded in Jungian psychology and emotional integration'
      ],
      ritualNote: 'Writing honestly helps surface what\'s hidden and transforms discomfort into awareness.'
    },
    {
      id: 'vision',
      title: 'Vision Quest',
      icon: 'Eye',
      color: 'from-blue-500/20 to-cyan-500/20',
      borderColor: 'border-blue-500/30',
      iconColor: 'text-blue-500',
      description: 'Practice gratitude as if your goals are already unfolding, and connect with your future self.',
      features: [
        'Future self visualization',
        'Gratitude practice grounded in neuroplasticity research',
        'Journaling reinforces belief and embodiment',
        'Builds trust in what you are becoming'
      ],
      ritualNote: 'Journaling reinforces belief, embodiment, and trust in what you\'re becoming.'
    },
    {
      id: 'night',
      title: 'Night Reflections',
      icon: 'Moon',
      color: 'from-slate-500/20 to-gray-500/20',
      borderColor: 'border-slate-500/30',
      iconColor: 'text-slate-400',
      description: 'Close your day with clarity, integration, and emotional release before rest.',
      features: [
        'Daily wins recognition',
        'Gentle processing of challenges',
        'Emotional release before sleep',
        'Grounded in expressive writing research'
      ],
      ritualNote: 'Reflecting in your journal helps your mind process the day and release emotional weight.'
    }
  ];

  return (
    <section id="guides" ref={sectionRef} className="relative py-20 px-6 overflow-hidden">
      {/* Image1 texture strip */}
      <div className="absolute inset-x-0 top-0 h-64 pointer-events-none overflow-hidden">
        <img
          src="/image1.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-top opacity-[0.15] scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background" />
      </div>
      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Section Header */}
        <div id="guides-header" className={`text-center mb-16 transition-all duration-1000 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Four daily guides for{' '}
            <span className="text-gradient">your inner world</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Use all four or choose the ones that match your current season.
            Each ritual combines personalized guidance, your own voice, and journaling.
          </p>
        </div>

        {/* Interactive Guides Grid */}
        <div id="guides-grid" className="grid md:grid-cols-2 gap-6 mb-12">
          {guides?.map((guide, guideIndex) => (
            <div
              key={guide?.id}
              className={`glassmorphism p-8 rounded-3xl cursor-pointer transition-all duration-500 border-2 ${
                activeGuide === guide?.id 
                  ? `${guide?.borderColor} violet-glow scale-[1.02]` 
                  : 'border-transparent hover:border-primary/20 hover:scale-[1.02]'
              } ${cardsVisible?.includes(guideIndex) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${guideIndex * 120}ms` }}
              onClick={() => setActiveGuide(activeGuide === guide?.id ? null : guide?.id)}
            >
              {/* Guide Header */}
              <div className="flex items-center space-x-4 mb-6">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${guide?.color} flex items-center justify-center`}>
                  <Icon name={guide?.icon} size={28} className={guide?.iconColor} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">{guide?.title}</h3>
                  <p className="text-sm text-muted-foreground">{guide?.description}</p>
                </div>
              </div>

              {/* Expandable Content */}
              <div className={`transition-all duration-300 overflow-hidden ${
                activeGuide === guide?.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="space-y-4">
                  {/* Features List */}
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">
                      What You'll Experience
                    </h4>
                    <ul className="space-y-2">
                      {guide?.features?.map((feature, index) => (
                        <li key={index} className="flex items-start space-x-2 text-sm text-muted-foreground">
                          <Icon name="Check" size={16} className="text-accent mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Ritual Note */}
                  <div className="bg-background/50 p-4 rounded-xl border border-primary/20">
                    <h4 className="text-sm font-semibold text-primary mb-2 flex items-center">
                      <Icon name="BookOpen" size={16} className="mr-2" />
                      Journaling
                    </h4>
                    <p className="text-sm text-muted-foreground italic">
                      {guide?.ritualNote}
                    </p>
                  </div>

                  {/* Progress Indicator */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Typical session: 5-10 minutes</span>
                    <div className="flex space-x-1">
                      {[...Array(5)]?.map((_, i) => (
                        <div key={i} className="w-2 h-2 bg-primary/30 rounded-full" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Expand/Collapse Indicator */}
              <div className="flex justify-center mt-4">
                <Icon 
                  name={activeGuide === guide?.id ? "ChevronUp" : "ChevronDown"} 
                  size={20} 
                  className="text-muted-foreground" 
                />
              </div>
            </div>
          ))}
        </div>

        {/* Integration Message */}
        <div id="guides-integration" className={`text-center transition-all duration-1000 ${integrationVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="glassmorphism p-8 rounded-3xl max-w-4xl mx-auto">
            <Icon name="Infinity" size={48} className="text-primary mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Listen. Repeat. Stay present. Journal.
            </h3>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Small daily rituals — real change. Each guide delivers personalized guidance in your own voice,
              followed by reflection prompts that help the new thought take root.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-accent">
              <div className="flex items-center space-x-2">
                <Icon name="Mic" size={16} />
                <span>Guidance in your own voice</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="RefreshCw" size={16} />
                <span>Built around your real situation</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="TrendingUp" size={16} />
                <span>Streaks and progress tracking</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GuidesSection;